import React, { useMemo, useState } from 'react';
import { Box, Typography, IconButton, Divider } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PricingControlsTable from '~/components/AdminPortal/PricingControls/SharedTable';
import Tooltip from '@mui/material/Tooltip';
import { ContactInfoCIcon } from '~/assets/images';
import TruncatedText from '~/components/General/TruncatedText';
import '../styles.scss';
import { balanceFormatter } from '~/utils/helperFunctions';
import { dateFormat } from '~/utils/date-format';
import { DEFAULT_PAGINATION } from '~/utils/constants';

const buildBaseColumns = (isWideScreen) => [
  {
    field: 'label',
    headerName: 'Product',
    flex: isWideScreen ? 2 : 0,
    width: 180,
    sortable: false,
    renderCell: (params) => (
      <>
        {isWideScreen ? (
          <Box className="fullText">
            <TruncatedText text={params.row.label || ''} />
          </Box>
        ) : (
          <Box className="addressWrapper">
            <TruncatedText text={params.row.label || ''} />
          </Box>
        )}
      </>
    ),
  },
  {
    field: 'cost',
    headerName: 'Cost of Goods',
    renderHeader: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Typography sx={{ fontWeight: 600, fontSize: 16 }}>Cost of Goods</Typography>
        <Tooltip title="Excludes postage cost" placement="bottom-start">
          <img src={ContactInfoCIcon} alt="info" style={{ cursor: 'pointer' }} />
        </Tooltip>
      </Box>
    ),
    flex: isWideScreen ? 1 : 0,
    width: 180,
    sortable: false,
    renderCell: (params) => (
      <Box className="addressWrapper">
        <Typography>{balanceFormatter(params?.row?.cost, 3) || ''}</Typography>
      </Box>
    ),
  },
  {
    field: 'change',
    headerName: 'Change',
    flex: isWideScreen ? 1 : 0,
    width: 150,
    sortable: false,
    renderCell: (params) => (
      <Box className="addressWrapper">
        <Typography>
          {params?.row?.change == null
            ? 'Calculating...'
            : balanceFormatter(params?.row?.change, 3) || ''}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'is_applicable',
    headerName: 'Update Pricing',
    flex: isWideScreen ? 1 : 0,
    width: 170,
    sortable: false,
    renderCell: (params) => (
      <Box className="addressWrapper">
        <Typography>{params?.row?.is_applicable === true ? 'Yes' : 'No'}</Typography>
      </Box>
    ),
  },
  {
    field: 'additional_increase',
    headerName: "Add'l Increase",
    flex: isWideScreen ? 1 : 0,
    width: 170,
    sortable: false,
    renderCell: (params) => (
      <Box className="addressWrapper">
        <Typography>{balanceFormatter(params?.row?.additional_increase, 3) || ''}</Typography>
      </Box>
    ),
  },
  {
    field: 'effective_date',
    headerName: 'Effective Date',
    flex: isWideScreen ? 1 : 0,
    width: 180,
    sortable: false,
    renderCell: (params) => (
      <Box className="addressWrapper">
        <Typography>{dateFormat(params?.row?.effective_date, true, true) || ' -'}</Typography>
      </Box>
    ),
  },
  {
    field: 'status_label',
    headerName: 'Status',
    flex: isWideScreen ? 1 : 0,
    width: 140,
    sortable: false,
    renderCell: (params) => (
      <Box className="addressWrapper">
        <Typography>{params?.row?.status_label || ' -'}</Typography>
      </Box>
    ),
  },
];

const buildColumns = (onOpenRowMenu, isWideScreen, activeRowId, menuOpen) => [
  ...buildBaseColumns(isWideScreen),
  {
    field: 'action',
    headerName: 'Action',
    width: 100,
    sortable: false,
    renderCell: (params) => {
      const isActive = menuOpen && params.row.id === activeRowId;
      return (
        <Box>
          {params?.row?.status !== 'active' && (
            <IconButton
              disableRipple={true}
              onClick={(e) => onOpenRowMenu(e, params.row)}
              sx={{
                opacity: isActive ? 0.8 : 0.4,
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            >
              <MoreVertIcon />
            </IconButton>
          )}
        </Box>
      );
    },
  },
];

const CategoryTable = ({
  category = 'Postcards',
  onOpenRowMenu,
  isWideScreen,
  activeRowId,
  menuOpen,
  data = [],
  loading = false,
  historicalLoading = false,
  historicalData = [],
  pagination = {},
  updatePagination,
  historicalPagination = {},
  updateHistoricalPagination,
}) => {
  const columns = useMemo(
    () => buildColumns(onOpenRowMenu, isWideScreen, activeRowId, menuOpen),
    [onOpenRowMenu, isWideScreen, activeRowId, menuOpen]
  );
  const columnsWithoutActions = useMemo(() => buildBaseColumns(isWideScreen), [isWideScreen]);
  // Use only API response data
  const rows = data || [];
  // Use historical data from props
  const historicalRows = historicalData || [];

  // Show loading message when no data and loading
  const showLoadingMessage = loading && rows.length === 0;

  const shouldShowPagination = (totalRecords, pageSize = DEFAULT_PAGINATION.pageSize) => {
    const totalPages = Math.ceil(totalRecords / pageSize);

    return totalRecords >= pageSize || totalPages > 1;
  };

  return (
    <>
      <PricingControlsTable
        className="pricing-controls-table"
        rows={rows}
        columns={columns}
        pagination={pagination}
        updatePagination={updatePagination}
        showPagination={shouldShowPagination(pagination?.totalRecords || 0)}
        loading={loading}
      />
      <Divider className="olcAdminSectionDivider" />
      <Box className="sectionHeader historicalHeader">
        <Typography className="pricingControlsSectionTitle">Historical Cost of Goods</Typography>
      </Box>
      <PricingControlsTable
        className="pricing-controls-table"
        rows={historicalRows}
        columns={columnsWithoutActions}
        pagination={historicalPagination}
        updatePagination={updateHistoricalPagination}
        showPagination={shouldShowPagination(historicalPagination?.totalRecords || 0)}
        loading={historicalLoading}
      />
    </>
  );
};

export default CategoryTable;
